import { ImageOff, Loader2 } from "lucide-react";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { extractErrorMessage } from "@/lib/api-errors";
import type { Tone } from "@/lib/order-journal";
import { cn } from "@/lib/utils";

/** Мелкие общие блоки журнала отгрузки. */

const TONE: Record<Tone, string> = {
  ok: "bg-emerald-100 text-emerald-800",
  warn: "bg-amber-100 text-amber-800",
  muted: "bg-muted text-muted-foreground",
  bad: "bg-rose-100 text-rose-800",
};

export function StatusBadge({
  tone,
  children,
}: {
  tone: Tone;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className={cn("inline-block rounded px-1.5 py-0.5 text-[11px] font-medium", TONE[tone])}>
      {children}
    </span>
  );
}

/**
 * Кеш object URL картинок на время жизни страницы: превью не перекачиваются,
 * когда карточка паллеты пропадает и возвращается (фильтр по товару), а при
 * уходе со страницы все URL отзываются. Один и тот же запрос в полёте — один.
 */
export class ObjectUrlCache {
  private urls = new Map<string, Promise<string>>();

  get(key: string, load: () => Promise<string>): Promise<string> {
    let p = this.urls.get(key);
    if (!p) {
      p = load();
      this.urls.set(key, p);
      // Сбой не кешируем: следующий показ превью попробует снова.
      p.catch(() => this.urls.delete(key));
    }
    return p;
  }

  revokeAll(): void {
    const all = [...this.urls.values()];
    this.urls.clear();
    for (const p of all) p.then((u) => URL.revokeObjectURL(u)).catch(() => {});
  }
}

const UrlCacheContext = createContext<ObjectUrlCache | null>(null);

export function UrlCacheProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [cache] = useState(() => new ObjectUrlCache());
  useEffect(() => () => cache.revokeAll(), [cache]);
  return <UrlCacheContext.Provider value={cache}>{children}</UrlCacheContext.Provider>;
}

function useUrlCache(): ObjectUrlCache {
  const c = useContext(UrlCacheContext);
  if (!c) throw new Error("UrlCacheProvider не подключён");
  return c;
}

/** Элемент попал (или вот-вот попадёт) в видимую область — один раз. */
function useInView<T extends Element>(rootMargin = "300px"): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, rootMargin]);
  return [ref, seen];
}

/**
 * Превью фото: грузится, только когда карточка у экрана (60 паллет × 3 фото
 * разом положили бы сеть склада), ошибка — значок с текстом в подсказке, а не
 * вечный спиннер.
 */
export function LazyThumb({
  cacheKey,
  load,
  onClick,
  label,
}: {
  cacheKey: string;
  load: () => Promise<string>;
  onClick: () => void;
  label: string;
}): React.ReactElement {
  const cache = useUrlCache();
  const [ref, inView] = useInView<HTMLButtonElement>();
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    cache
      .get(cacheKey, load)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(extractErrorMessage(e, "Не удалось загрузить превью"));
      });
    return () => {
      cancelled = true;
    };
    // `load` — новая стрелка на каждый рендер; запрос определяет ключ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, cacheKey, cache]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={label}
      title={error || label}
      className="size-20 shrink-0 overflow-hidden rounded-md border bg-muted transition-opacity hover:opacity-90 sm:size-24"
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : error ? (
        <span className="flex size-full flex-col items-center justify-center gap-1 p-1 text-[10px] leading-tight text-rose-700">
          <ImageOff className="size-5" />
          ошибка
        </span>
      ) : (
        <span className="flex size-full items-center justify-center text-muted-foreground">
          {inView && <Loader2 className="size-4 animate-spin" />}
        </span>
      )}
    </button>
  );
}
